import React, { useEffect, useState } from 'react';
import { Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateEmployee } from '../../hooks/employees/useCreateEmployee';
import { useUpdateEmployee } from '../../hooks/employees/useUpdateEmployee';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import { useGetJobTitles } from '../../hooks/jobTitles/useGetJobTitles';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditEmployee = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createEmployee, isPending: isCreating } = useCreateEmployee();
    const { editEmployee, isUpdate } = useUpdateEmployee();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);
    const { departments } = useGetDepartments(1, 999);
    const { jobTitles } = useGetJobTitles(1, 999);

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        school_id: '', department_id: '', job_title_id: '',
        code: '', full_name: '', gender: '',
        phone: '', national_id: '',
        date_of_birth: '', address: '', salary: '', hire_date: '',
        is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            school_id: currentData.school_id || '',
            department_id: currentData.department_id || '',
            job_title_id: currentData.job_title_id || '',
            code: currentData.code || '',
            full_name: currentData.full_name || '',
            gender: currentData.gender || '',
            phone: currentData.phone || '',
            national_id: currentData.national_id || '',
            date_of_birth: currentData.date_of_birth || '',
            address: currentData.address || '',
            salary: currentData.salary ?? '',
            hire_date: currentData.hire_date || '',
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') { setForm((p) => ({ ...p, [name]: checked })); return; }
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            school_id: Number(form.school_id),
            department_id: form.department_id ? Number(form.department_id) : null,
            job_title_id: form.job_title_id ? Number(form.job_title_id) : null,
            code: form.code || null,
            full_name: form.full_name,
            gender: form.gender,
            phone: form.phone || null,
            national_id: form.national_id || null,
            date_of_birth: form.date_of_birth || null,
            address: form.address || null,
            salary: form.salary !== '' ? Number(form.salary) : null,
            hire_date: form.hire_date || null,
            is_active: !!form.is_active,
        };
        if (isEdit && currentData?.id) {
            editEmployee({ req: payload, id: currentData.id });
        } else {
            createEmployee(payload);
        }
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOpts = (schools || []).map((s) => ({ id: s.id, name: s.name }));
    const deptOpts = (departments || [])
        .filter((d) => !form.school_id || d.school_id === Number(form.school_id))
        .map((d) => ({ id: d.id, name: d.name }));
    const jtOpts = (jobTitles || [])
        .filter((j) => !form.department_id || j.department_id === Number(form.department_id))
        .map((j) => ({ id: j.id, name: j.name }));

    const genderOpts = [
        { id: 'male', name: isArabic ? 'ذكر' : 'Male' },
        { id: 'female', name: isArabic ? 'أنثى' : 'Female' },
    ];

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المدرسة' : 'School'} type="select" name="school_id"
                            value={form.school_id}
                            onChange={(e) => setForm((p) => ({ ...p, school_id: e.target.value, department_id: '', job_title_id: '' }))}
                            options={schoolOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'القسم' : 'Department'} type="select" name="department_id"
                            value={form.department_id}
                            onChange={(e) => setForm((p) => ({ ...p, department_id: e.target.value, job_title_id: '' }))}
                            options={[{ id: '', name: isArabic ? 'بدون قسم' : 'None' }, ...deptOpts]} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المسمى الوظيفي' : 'Job Title'} type="select" name="job_title_id"
                            value={form.job_title_id}
                            onChange={(e) => setForm((p) => ({ ...p, job_title_id: e.target.value }))}
                            options={[{ id: '', name: isArabic ? 'بدون مسمى' : 'None' }, ...jtOpts]} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الكود' : 'Code'} placeholder="EMP-001"
                            type="text" name="code" value={form.code} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الاسم الكامل' : 'Full Name'}
                            placeholder={isArabic ? 'ادخل الاسم' : 'Enter full name'}
                            type="text" name="full_name" value={form.full_name} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الجنس' : 'Gender'} type="select" name="gender"
                            value={form.gender} onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
                            options={genderOpts} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الهاتف' : 'Phone'} type="text" name="phone"
                            value={form.phone} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'رقم البطاقة' : 'National ID'} type="text" name="national_id"
                            value={form.national_id} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'تاريخ الميلاد' : 'Date of Birth'} type="date" name="date_of_birth"
                            value={form.date_of_birth} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'تاريخ التعيين' : 'Hire Date'} type="date" name="hire_date"
                            value={form.hire_date} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الراتب' : 'Salary'} placeholder="0.00"
                            type="number" name="salary" value={form.salary} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'العنوان' : 'Address'} type="text" name="address"
                            value={form.address} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'مفعّل' : 'Active'} />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditEmployee;
